<template>
  <div class="container py-5">
    <div class="row justify-content-center">
      <div class="col-md-8">
        <div class="card">
          <div class="card-header register-card-header">
            <router-link
              to="/"
              class="register-logo-link"
            >
              <b-img
                class="small-logo-image"
                :src="companyLogoImage"
                alt="App logo"
              />
            </router-link>
            <h4 class="mb-0">Register your company</h4>
          </div>

          <div class="card-body">
            <form @submit.prevent="register">
              <div class="form-group">
                <label for="company_name">Company Name</label>
                <input
                    id="company_name"
                    v-model="company.name"
                    type="text"
                    class="form-control"
                    required
                >
              </div>

              <div class="form-group">
                <label for="company_email">Company Email (optional)</label>
                <input
                    id="company_email"
                    v-model="company.email"
                    type="email"
                    class="form-control"
                >
              </div>

              <div class="form-group">
                <label for="company_phone">Company Phone (optional)</label>
                <PhoneNumberInput
                  id="company_phone"
                  v-model:dial="companyDial"
                  v-model:number="companyNumber"
                  :preferred-country-id="company.country_id"
                />
              </div>

              <div class="form-group">
                <label for="company_description">Company Description (optional)</label>
                <textarea
                    id="company_description"
                    v-model="company.description"
                    class="form-control"
                    rows="4"
                ></textarea>
              </div>

              <div class="form-group">
                <label for="company_country">Company based country</label>
                <CountrySelect
                  input-id="company_country"
                  v-model="company.country_id"
                />
              </div>

              <div class="form-group pt-2">
                <label class="d-block pb-1">What is the main purpose of the company?</label>
                <div class="d-flex align-items-start">
                  <b-form-group
                    label="To sell goods"
                    label-for="register-purpose-sell"
                    class="mb-0 mr-3"
                  >
                    <b-form-checkbox
                      id="register-purpose-sell"
                      v-model="sellGoods"
                      switch
                      class="mb-1"
                    />
                  </b-form-group>
                  <b-form-group
                    label="To provide services"
                    label-for="register-purpose-service"
                    class="mb-0"
                  >
                    <b-form-checkbox
                      id="register-purpose-service"
                      v-model="provideServices"
                      switch
                      class="mb-1"
                    />
                  </b-form-group>
                </div>
              </div>

              <div class="form-group" v-show="false">
                <label for="company_region">Region (optional)</label>
                <RegionSelect
                  input-id="company_region"
                  v-model="company.region_id"
                  :country-id="company.country_id"
                />
              </div>

              <div class="form-group" v-show="false">
                <label for="company_picture">Company logo</label>
                <input
                    id="company_picture"
                    type="file"
                    class="form-control-file"
                    accept="image/*"
                    @change="onFileChange"
                >
              </div>

              <div >
                <h5 class="mb-2 mt-3">Company owner account</h5>
              </div>

              <div class="form-group">
                You will use this account to login to your company dashboard, create products, orders, customers, stock locations add staff members and more.
              </div>
              <div class="form-group">
                <label for="user_name">Full name</label>
                <input
                    id="user_name"
                    v-model="user.name"
                    type="text"
                    class="form-control"
                    required
                >
              </div>

              <div class="form-group">
                <label for="user_email">Email</label>
                <input
                    id="user_email"
                    v-model="user.email"
                    type="email"
                    class="form-control"
                    required
                    placeholder="You will use this email to login to your account"
                >
              </div>

              <div class="form-group">
                <label for="user_password">Password</label>
                <input
                    id="user_password"
                    v-model="user.password"
                    type="password"
                    class="form-control"
                    required
                >
              </div>

              <div class="form-group">
                <label for="user_password_repeat">Repeat Password</label>
                <input
                    id="user_password_repeat"
                    v-model="user.password_repeat"
                    type="password"
                    class="form-control"
                    required
                >
              </div>

              <div class="form-group">
                <div class="custom-control custom-checkbox">
                  <input
                    id="privacy_policy"
                    v-model="acceptedPrivacyPolicy"
                    type="checkbox"
                    class="custom-control-input"
                    required
                  >
                  <label
                    class="custom-control-label register-privacy-label"
                    for="privacy_policy"
                  >
                    By registering, you agree to our
                    <a
                      href="#"
                      class="register-privacy-link"
                      @click.prevent="showPrivacyPolicyModal"
                    >Privacy Policy</a>
                    and acknowledge how we collect and use your company and account information.
                  </label>
                </div>
              </div>

              <MathCaptchaField
                ref="registerCaptcha"
                input-id="register_captcha"
                :captcha-token="captchaToken"
                :captcha-answer="captchaAnswer"
                @update:captchaToken="captchaToken = $event"
                @update:captchaAnswer="captchaAnswer = $event"
              />

              <div class="form-group pt-2 d-flex align-items-center justify-content-between flex-wrap">
                <router-link to="/login">
                  Already have an account? Log in here
                </router-link>
                <button type="submit" class="btn btn-primary mt-1" :disabled="loading || !acceptedPrivacyPolicy">
                  {{ loading ? 'Registering...' : 'Register' }}
                </button>
              </div>
            </form>

            <div v-if="error" class="alert alert-danger mt-3">
              {{ error }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <b-modal
      id="register-privacy-policy-modal"
      v-model="privacyPolicyModalVisible"
      title="Privacy Policy"
      size="lg"
      scrollable
      hide-header-close
      ok-only
      ok-title="Close"
      ok-variant="primary"
      centered
      body-class="register-privacy-modal-body"
    >
      <div class="register-privacy-content">
        <p><strong>Last updated:</strong> July 22, 2026</p>
        <p>
          This Privacy Policy explains how we collect, use, store, and share information when you
          register a company account and use our platform. This is placeholder text for demonstration
          purposes and will be replaced with the final legal policy later.
        </p>

        <h5>1. Information we collect</h5>
        <p>
          When you create an account, we may collect company details such as company name, contact
          email, phone number, country, and description, as well as account owner details such as full
          name, email address, and password. We may also collect technical information related to your
          access, including IP address, browser user agent, referrer, host, request URI, and query
          string parameters for security and audit purposes.
        </p>
        <p>
          Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut
          labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco
          laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in
          voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat
          non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.
        </p>

        <h5>2. How we use information</h5>
        <p>
          We use collected information to create and manage your company workspace, authenticate users,
          provide product and order management features, communicate service-related notices, prevent
          abuse, and improve reliability and security of the service.
        </p>
        <p>
          Phasellus viverra nulla ut metus varius laoreet. Quisque rutrum. Aenean imperdiet. Etiam
          ultricies nisi vel augue. Curabitur ullamcorper ultricies nisi. Nam eget dui. Etiam rhoncus.
          Maecenas tempus, tellus eget condimentum rhoncus, sem quam semper libero, sit amet adipiscing
          sem neque sed ipsum. Nam quam nunc, blandit vel, luctus pulvinar, hendrerit id, lorem.
        </p>
        <p>
          Maecenas nec odio et ante tincidunt tempus. Donec vitae sapien ut libero venenatis faucibus.
          Nullam quis ante. Etiam sit amet orci eget eros faucibus tincidunt. Duis leo. Sed fringilla
          mauris sit amet nibh. Donec sodales sagittis magna. Sed consequat, leo eget bibendum sodales,
          augue velit cursus nunc.
        </p>

        <h5>3. Sharing and disclosure</h5>
        <p>
          We do not sell your personal information. We may share limited data with trusted service
          providers that help us operate the platform (for example hosting, email delivery, or payment
          processing), and only as needed to provide those services. We may also disclose information
          when required by law or to protect the rights, property, or safety of our users.
        </p>
        <p>
          Integer tincidunt. Cras dapibus. Vivamus elementum semper nisi. Aenean vulputate eleifend
          tellus. Aenean leo ligula, porttitor eu, consequat vitae, eleifend ac, enim. Aliquam lorem
          ante, dapibus in, viverra quis, feugiat a, tellus. Phasellus viverra nulla ut metus varius
          laoreet. Quisque rutrum. Aenean imperdiet. Etiam ultricies nisi vel augue.
        </p>

        <h5>4. Data retention and security</h5>
        <p>
          We retain account and company information for as long as your account remains active or as
          needed to provide the service and meet legal obligations. We apply reasonable technical and
          organizational measures to protect information against unauthorized access, alteration,
          disclosure, or destruction.
        </p>
        <p>
          Curabitur ullamcorper ultricies nisi. Nam eget dui. Etiam rhoncus. Maecenas tempus, tellus
          eget condimentum rhoncus, sem quam semper libero, sit amet adipiscing sem neque sed ipsum.
          Nam quam nunc, blandit vel, luctus pulvinar, hendrerit id, lorem. Maecenas nec odio et ante
          tincidunt tempus. Donec vitae sapien ut libero venenatis faucibus. Nullam quis ante.
        </p>
        <p>
          Etiam sit amet orci eget eros faucibus tincidunt. Duis leo. Sed fringilla mauris sit amet
          nibh. Donec sodales sagittis magna. Sed consequat, leo eget bibendum sodales, augue velit
          cursus nunc, quis gravida magna mi a libero. Fusce vulputate eleifend sapien. Vestibulum
          purus quam, scelerisque ut, mollis sed, nonummy id, metus.
        </p>

        <h5>5. Your choices</h5>
        <p>
          Depending on your location, you may have rights to access, correct, update, or request
          deletion of personal information associated with your account. You may also contact us to
          ask questions about this Privacy Policy or to exercise applicable privacy rights.
        </p>
        <p>
          Nullam dictum felis eu pede mollis pretium. Integer tincidunt. Cras dapibus. Vivamus
          elementum semper nisi. Aenean vulputate eleifend tellus. Aenean leo ligula, porttitor eu,
          consequat vitae, eleifend ac, enim. Aliquam lorem ante, dapibus in, viverra quis, feugiat a,
          tellus. Phasellus viverra nulla ut metus varius laoreet.
        </p>

        <h5>6. Changes to this policy</h5>
        <p>
          We may update this Privacy Policy from time to time. When we do, we will revise the “Last
          updated” date above. Continued use of the service after updates become effective means you
          acknowledge the revised policy.
        </p>
        <p>
          Quisque rutrum. Aenean imperdiet. Etiam ultricies nisi vel augue. Curabitur ullamcorper
          ultricies nisi. Nam eget dui. Etiam rhoncus. Maecenas tempus, tellus eget condimentum
          rhoncus, sem quam semper libero, sit amet adipiscing sem neque sed ipsum. Nam quam nunc,
          blandit vel, luctus pulvinar, hendrerit id, lorem. Maecenas nec odio et ante tincidunt
          tempus.
        </p>
        <p>
          Donec vitae sapien ut libero venenatis faucibus. Nullam quis ante. Etiam sit amet orci eget
          eros faucibus tincidunt. Duis leo. Sed fringilla mauris sit amet nibh. Donec sodales sagittis
          magna. Sed consequat, leo eget bibendum sodales, augue velit cursus nunc, quis gravida magna
          mi a libero. Fusce vulputate eleifend sapien. Vestibulum purus quam, scelerisque ut, mollis
          sed, nonummy id, metus. Nullam accumsan lorem in dui.
        </p>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { BModal, BFormCheckbox, BFormGroup } from 'bootstrap-vue'
import { sweetError } from '@/libs/sweet-alerts';
import { $themeConfig } from "@themeConfig";
import { buildFullPhone, normalizeDialCode } from '@/libs/phoneNumber'
import { getBrowserAccessContext } from '@/libs/accessLogContext'
import CountrySelect from '@/views/Components/Elements/CountrySelect.vue'
import RegionSelect from '@/views/Components/Elements/RegionSelect.vue'
import PhoneNumberInput from '@/views/Components/Elements/PhoneNumberInput.vue'
import MathCaptchaField from '@/views/Components/Elements/MathCaptchaField.vue'

export default {
  name: 'Register',
  components: {
    BModal,
    BFormCheckbox,
    BFormGroup,
    CountrySelect,
    RegionSelect,
    PhoneNumberInput,
    MathCaptchaField,
  },
  data() {
    const { companyLogoImage } = $themeConfig.app;
    return {
      companyLogoImage,
      loading: false,
      error: null,
      acceptedPrivacyPolicy: false,
      privacyPolicyModalVisible: false,
      sellGoods: true,
      provideServices: false,
      companyDial: '1',
      companyNumber: '',
      company: {
        name: '',
        email: '',
        description: '',
        country_id: null,
        region_id: null,
        picture: null,
        city: '',
        address_line_1: '',
        address_line_2: '',
        zip_code: '',
        purpose: 'sell',
        tax_value: 0.00,
        tax_name: '',
        tax_id: ''
      },
      user: {
        name: '',
        email: '',
        password: '',
        password_repeat: '',
      },
      captchaToken: '',
      captchaAnswer: '',
    };
  },
  computed: {
    ...mapGetters({
      countries: 'companies/countries',
    }),
  },
  watch: {
    'company.country_id'(countryId) {
      this.syncDialFromCountry(countryId)
    },
    sellGoods(value) {
      if (value) {
        this.provideServices = false
        this.company.purpose = 'sell'
      }
    },
    provideServices(value) {
      if (value) {
        this.sellGoods = false
        this.company.purpose = 'service'
      }
    },
  },
  methods: {
    showPrivacyPolicyModal() {
      this.privacyPolicyModalVisible = true
    },

    syncDialFromCountry(countryId) {
      if (!countryId || !Array.isArray(this.countries)) {
        return
      }

      const country = this.countries.find(
        (item) => Number(item.id) === Number(countryId),
      )
      const dial = normalizeDialCode(country?.phone_code)
      if (dial && dial !== '0') {
        this.companyDial = dial
      }
    },

    onFileChange(event) {
      const file = event.target.files[0] || null;
      this.company.picture = file;
    },

    async register() {
      this.error = null;

      if (!this.acceptedPrivacyPolicy) {
        sweetError('Please accept the Privacy Policy to continue', 'Please read our Privacy Policy and agree to the terms and conditions.');
        return;
      }

      if (this.user.password !== this.user.password_repeat) {
        sweetError('Passwords do not match', 'Passwords must be the same and must be at least 8 characters long contain at least one uppercase letter, one lowercase letter, one number and one special character.');
        return;
      }

      if (!this.company.country_id) {
        sweetError('Please select a country', 'Choose the country where your company is located from the list.');
        return;
      }

      if (!this.sellGoods && !this.provideServices) {
        sweetError(
          'Please choose the main purpose of the company',
          'Select sell goods or provide services.',
        );
        return;
      }

      if (!this.captchaToken) {
        sweetError('Error', 'Please refresh the question and try again.');
        return;
      }

      this.loading = true;

      try {
        const phone = String(this.companyNumber || '').trim()
          ? buildFullPhone(this.companyDial, this.companyNumber)
          : '';

        const companyFormData = new FormData();
        companyFormData.append('name', this.company.name);
        companyFormData.append('email', this.company.email);
        companyFormData.append('phone', phone);
        companyFormData.append('description', this.company.description);
        companyFormData.append('country_id', String(this.company.country_id));
        companyFormData.append('purpose', this.provideServices ? 'service' : 'sell');
        if (this.company.region_id != null && this.company.region_id !== '') {
          companyFormData.append('region_id', String(this.company.region_id));
        }
        companyFormData.append('city', this.company.city || '');
        companyFormData.append('address_line_1', this.company.address_line_1 || '');
        companyFormData.append('address_line_2', this.company.address_line_2 || '');
        companyFormData.append('zip_code', this.company.zip_code || '');

        companyFormData.append('user_name', this.user.name);
        companyFormData.append('user_email', this.user.email);
        companyFormData.append('user_password', this.user.password);
        companyFormData.append('captcha_token', this.captchaToken);
        companyFormData.append('captcha_answer', String(Number(this.captchaAnswer)));

        const accessContext = getBrowserAccessContext();
        companyFormData.append('http_host', accessContext.http_host);
        companyFormData.append('request_uri', accessContext.request_uri);
        companyFormData.append('query_string', accessContext.query_string);

        if (this.company.picture) {
          companyFormData.append('picture', this.company.picture);
        }

        await this.$store.dispatch('auth/sanctum');
        await this.$store.dispatch('companies/registerCompany', {
          formData: companyFormData,
        }).then((companyResponse) => {

          const createdCompany = companyResponse && companyResponse.data && companyResponse.data.data
              ? companyResponse.data.data
              : companyResponse.data;

          const companyId = createdCompany.id;
          this.$router.push({ path: '/login', query: { registered: '1' } });
        });

      } catch (e) {
        const responseData = e && e.response ? e.response.data : null;
        if (responseData) {
          let errorMSG = (responseData.message || 'Registration failed.') + '</br>';
          if (responseData.errors) {
            Object.keys(responseData.errors).map((errorKey) => {
              responseData.errors[errorKey].map((value) => {
                errorMSG += value + '</br>';
              });
            });
          }
          sweetError('Error', errorMSG);
        } else {
          sweetError('Error', 'Registration failed. Please try again.');
        }
        this.$refs.registerCaptcha?.refresh();
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
<style scoped>
.register-card-header {
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.register-logo-link {
  display: inline-flex;
}

.small-logo-image {
  width: 100px;
  max-width: 100%;
  height: auto;
  margin-bottom: 0.75rem;
}

.register-privacy-label {
  font-size: 0.925rem;
  line-height: 1.45;
}

.register-privacy-link {
  font-weight: 600;
  text-decoration: underline;
}

.register-privacy-content h5 {
  margin-top: 1.25rem;
  margin-bottom: 0.5rem;
}

.register-privacy-content p {
  margin-bottom: 0.85rem;
}
</style>

<style>
/* Modal body is teleported outside this component; keep scroll height usable. */
.register-privacy-modal-body {
  max-height: min(70vh, 560px);
  overflow-y: auto;
}
</style>
